/**
 * Meine Meldungen (spec 10.3): the teacher's own flags with their status and the operator's
 * note. There are no notifications in the pilot — the operator checks `flags list` daily and
 * the teacher looks here for the ruling.
 *
 * A flag whose card or question a rewrite has since removed keeps its ruling; the API sends
 * "(umgeschrieben)" as the target text for that case (spec 4.4).
 */

import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { authedJson } from "../shared/api.js";
import type { Flag } from "../shared/api-types.js";
import { germanMessage } from "../shared/errors.js";
import { FLAG_REASON_LABELS, FLAG_STATUS_LABELS } from "./labels.js";
import { Empty, ErrorLine, Loading, Panel, formatDate } from "./ui.js";

const STATUS_STYLE: Record<Flag["status"], string> = {
  open: "bg-[#fef9c3] text-[#854d0e]",
  kept: "bg-[#f5f5f4] text-[var(--color-muted)]",
  rewritten: "bg-[#dcfce7] text-[var(--color-ok)]",
};

export function FlagsScreen() {
  const flags = useQuery({
    queryKey: ["teacher", "flags"],
    queryFn: () => authedJson<Flag[]>("/teacher/flags", { subjectType: "teacher" }),
  });

  if (flags.isPending) return <Loading />;
  if (flags.isError) return <ErrorLine>{germanMessage(flags.error)}</ErrorLine>;

  return (
    <>
      <h1 className="mt-0 mb-5 text-[26px] font-bold">Meine Meldungen</h1>
      <Panel
        title={flags.data?.length === 1 ? "1 Meldung" : (flags.data?.length ?? 0) + " Meldungen"}
      >
        {flags.data && flags.data.length === 0 ? (
          <Empty>
            Noch nichts gemeldet. Wenn dir auf einer Karte oder bei einer Frage etwas auffällt,
            melde es unter «Themen».
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="t-table">
              <thead>
                <tr>
                  <th scope="col">Thema</th>
                  <th scope="col">Betrifft</th>
                  <th scope="col">Grund</th>
                  <th scope="col">Kommentar</th>
                  <th scope="col">Stand</th>
                  <th scope="col">Antwort von Scrolivo</th>
                  <th scope="col">Gemeldet</th>
                </tr>
              </thead>
              <tbody>
                {flags.data?.map((flag) => (
                  <tr key={flag.id}>
                    <td>
                      <Link
                        to={"/lehrer/themen/" + flag.topic.slug}
                        className="font-semibold text-[var(--color-accent)] no-underline hover:underline"
                      >
                        {flag.topic.title}
                      </Link>
                    </td>
                    <td>
                      <span className="text-[13px] text-[var(--color-muted)]">
                        {flag.target.kind === "card" ? "Karte" : "Frage"}
                      </span>
                      <br />
                      {flag.target.text}
                    </td>
                    <td>{FLAG_REASON_LABELS[flag.reason]}</td>
                    <td className="max-w-xs">{flag.comment ?? "—"}</td>
                    <td>
                      <span
                        className={
                          "inline-block rounded-full px-2.5 py-0.5 text-[13px] font-semibold " +
                          STATUS_STYLE[flag.status]
                        }
                      >
                        {FLAG_STATUS_LABELS[flag.status]}
                      </span>
                    </td>
                    <td className="max-w-xs">{flag.operatorNote ?? "—"}</td>
                    <td className="whitespace-nowrap">{formatDate(flag.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
